import posthog from 'posthog-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import { API_URL } from './config';
import EarningsCalendar from './components/EarningsCalendar';
import EventCard from './components/EventCard';
import Get15TPCModal from './components/Get15TPCModal';
import Get5TPCModal from './components/Get5TPCModal';
import Header from './components/Header';
import HowItWorksModal from './components/HowItWorksModal';
import LoadingDots from './components/LoadingDots';
import LoadingSpinner from './components/LoadingSpinner';
import LoginModal from './components/LoginModal';
import MarketSelectionModal from './components/MarketSelectionModal';
import MentionsEventCard from './components/MentionsEventCard';
import NotificationBanner from './components/NotificationBanner';
import OAuthCallback from './components/OAuthCallback';
import ParlayConfirmation from './components/ParlayConfirmation';
import ParlayConfirmationModal from './components/ParlayConfirmationModal';
import Parlays from './components/Parlays';
import ParlaySuggestionsCarousel from './components/ParlaySuggestionsCarousel';
import Profile from './components/Profile';
import PurchaseModal from './components/PurchaseModal';
import SportsEventCard from './components/SportsEventCard';
import SportsNavSidebar from './components/SportsNavSidebar';
import FinanceNavSidebar from './components/FinanceNavSidebar';
import CryptoNavSidebar from './components/CryptoNavSidebar';
import TotalAmountModal from './components/TotalAmountModal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useActiveParlaysCount } from './hooks/useActiveParlaysCount';
import { fetchEventCards, fetchParlaySuggestions, fetchSportsEvents, fetchSportsProps, ParlaySuggestion } from './services/api';
import { EventCard as EventCardType, SportsEvent } from './types';
import { processMarketQuestions } from './utils/marketQuestionProcessor';
import { groupEventsWithPinned, isGameLive } from './utils/sportsUtils';

function AppContent() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { count: activeParlaysCount, refetch: refetchParlaysCount } = useActiveParlaysCount();
  const [eventCards, setEventCards] = useState<EventCardType[]>([]);
  const [sportsEvents, setSportsEvents] = useState<SportsEvent[]>([]);
  const [sportsProps, setSportsProps] = useState<EventCardType[]>([]);
  const [sportsViewMode, setSportsViewMode] = useState<'games' | 'props'>('games');
  const [hasLiveGames, setHasLiveGames] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [propsCurrentPage, setPropsCurrentPage] = useState(1);
  const [propsHasMore, setPropsHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<string>('Trending');
  const [referralCode, setReferralCode] = useState<string | undefined>(undefined);
  const [pinnedEvents, setPinnedEvents] = useState<Set<string>>(new Set());
  const [parlaySuggestions, setParlaySuggestions] = useState<ParlaySuggestion[]>([]);

  // Finance sidebar counts
  const { financeCounts, financeTotal } = useMemo(() => {
    try {
      const financeEventsAll = (eventCards || []).filter((e: any) => (e as any).category === 'Finance');
      const counts: Record<string, number> = {};
      for (const e of financeEventsAll) {
        const sub = (e as any).sub_category || '';
        counts[sub] = (counts[sub] || 0) + 1;
      }
      return { financeCounts: counts, financeTotal: financeEventsAll.length };
    } catch {
      return { financeCounts: {}, financeTotal: undefined as any };
    }
  }, [eventCards]);

  // Parse referral code from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      // Clear URL parameter after capturing
      window.history.replaceState({}, '', window.location.pathname);
      console.log('Captured referral code from URL:', refCode);
    }
  }, []);
  
  // Reset sub-category and filter when primary category changes
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    // Default to "All" (empty string) for Sports, otherwise reset to "All"
    setSelectedSubCategory('');
    setSelectedFilter(''); // Reset filter
    // Reset pagination state for both games and props
    setCurrentPage(1);
    setHasMore(true);
    setPropsCurrentPage(1);
    setPropsHasMore(true);
  };
  const [selectedEvent, setSelectedEvent] = useState<EventCardType | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<'yes' | 'no'>('yes');
  const [showMarketModal, setShowMarketModal] = useState<boolean>(false);
  const [addingLegLoading, setAddingLegLoading] = useState<boolean>(false);
  
  // Purchase Modal state
  const [showPurchaseModal, setShowPurchaseModal] = useState<boolean>(false);
  const [purchaseModalData, setPurchaseModalData] = useState<{
    eventTitle: string;
    marketQuestion: string;
    outcome: 'yes' | 'no';
    outcomePrice: number;
    marketId: string;
    endDate?: string;
    marketIcon?: string;
    eventImage?: string;
    sportsTeamData?: any;
    propsLabels?: { yes: string; no: string };
  } | null>(null);
  
  const [parlayLegs, setParlayLegs] = useState<Array<{
    // Remove id field - no more database storage until confirmation
    eventTitle: string;
    marketQuestion: string;
    outcome: 'yes' | 'no';
    outcomePrice: number; // Original price when leg was added
    decimalOdds: number;
    endDate?: string;
    marketIcon?: string;
    marketId: string; // Required for quote API
    sportsTeamData?: any;
  }>>([]);

  // Quote state will be managed in final phase when user enters total amount

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showActiveParlays, setShowActiveParlays] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showTotalAmountModal, setShowTotalAmountModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [quotingLoading, setQuotingLoading] = useState(false);
  const [currentQuote, setCurrentQuote] = useState<{
    quote_id: string;
    total_odds: number;
    expected_payout: number;
    expiry: string;
    total_amount: number;
  } | null>(null);

  // Stats for ParlayConfirmation - uses quote data when available
  const parlayStats = {
    total_amount: currentQuote?.total_amount || 0,
    total_odds: currentQuote?.total_odds || 1.0,
    expected_payout: currentQuote?.expected_payout || 0,
    legs_count: parlayLegs.length
  };
  const [processedQuestions, setProcessedQuestions] = useState<{ [key: string]: string }>({});
  const [isProcessingQuestions, setIsProcessingQuestions] = useState(false);
  const loadTriggerRef = useRef<HTMLDivElement>(null);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showGet5TPCModal, setShowGet5TPCModal] = useState(false);
  const [showGet15TPCModal, setShowGet15TPCModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isBottomBannerExpanded, setIsBottomBannerExpanded] = useState(false);
  const [isBottomBannerEditMode, setIsBottomBannerEditMode] = useState(false);
  const [selectedBottomBannerLegIndices, setSelectedBottomBannerLegIndices] = useState<Set<number>>(new Set());

  // Check for pending parlay data when authentication completes
  useEffect(() => {
    // Only check for parlay data after auth loading is complete
    if (isLoading) return;
    
    console.log('App.tsx: Checking for pending parlay data...');
    const pendingParlayData = sessionStorage.getItem('pendingParlayData');
    const authContext = sessionStorage.getItem('authContext');
    
    console.log('App.tsx: pendingParlayData exists:', !!pendingParlayData);
    console.log('App.tsx: authContext:', authContext);
    console.log('App.tsx: isAuthenticated:', isAuthenticated);
    console.log('App.tsx: isLoading:', isLoading);
    
    if (pendingParlayData && authContext === 'parlay-confirmation') {
      try {
        const parlayData = JSON.parse(pendingParlayData);
        console.log('App.tsx: Restoring parlay data:', parlayData);
        
        // Restore parlay legs and stats
        setParlayLegs(parlayData.legs);
        setShowConfirmation(true);
        
        // Clear the stored data
        sessionStorage.removeItem('pendingParlayData');
        sessionStorage.removeItem('authContext');
        
        console.log('App.tsx: Successfully restored parlay data and set showConfirmation to true');
      } catch (error) {
        console.error('App.tsx: Error restoring parlay data:', error);
        sessionStorage.removeItem('pendingParlayData');
        sessionStorage.removeItem('authContext');
      }
    } else {
      console.log('App.tsx: No parlay data to restore');
    }
  }, [isAuthenticated, isLoading]);

  // Check for needsReferralSignup flag after OAuth redirect
  useEffect(() => {
    console.log('App.tsx: Checking for needsReferralSignup flag...');
    const needsReferralSignup = sessionStorage.getItem('needsReferralSignup');
    const isSignup = sessionStorage.getItem('isSignup');
    console.log('App.tsx: sessionStorage flags:', { needsReferralSignup, isSignup });

    if (needsReferralSignup === 'true') {
      console.log('App.tsx: OAuth redirect requires referral signup - opening signup modal');
      // Clear the flag
      sessionStorage.removeItem('needsReferralSignup');
      // Open the login modal (which will show as signup modal due to isSignup flag)
      setShowLoginModal(true);
    }
  }, []); // Run once on mount

  // Function to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'TBD';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const isNextYear = date.getFullYear() > now.getFullYear();
      
      return date.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit',
        ...(isNextYear && { year: 'numeric' })
      });
    } catch {
      return 'TBD';
    }
  };

  // Function to check if a market is resolved or has extreme probability
  const isMarketResolvedOrExtreme = (market: any) => {
    const yesPrice = market.outcome_prices[0] || 0;
    const noPrice = market.outcome_prices[1] || 0;

    // Market is resolved/extreme if either outcome is ≤0.1% or ≥99.9%
    return yesPrice <= 0.001 || yesPrice >= 0.999 ||
           noPrice <= 0.001 || noPrice >= 0.999;
  };

  // Function to check if an event has only one active market (same logic as EventCard.tsx)
  const isSingleMarketEvent = (event: EventCardType) => {
    const activeMarkets = event.top_markets.filter(market => {
      const yesPrice = market.outcome_prices[0] || 0;
      const noPrice = market.outcome_prices[1] || 0;
      // Filter out resolved/extreme markets (≤0.1% or ≥99.9%)
      return !(yesPrice <= 0.001 || yesPrice >= 0.999 ||
               noPrice <= 0.001 || noPrice >= 0.999);
    });
    return activeMarkets.length === 1;
  };

  // Function to format number with commas (1000 -> 1,000)
  const formatNumberWithCommas = (num: number) => {
    if (num === 0) return '';
    return num.toLocaleString();
  };

  // Function to calculate settlement date (latest endDate across parlay legs)
  const calculateSettlementDate = (legs: Array<any>) => {
    if (legs.length === 0) return 'TBD';
    
    const validDates = legs
      .map(leg => leg.endDate ? new Date(leg.endDate).getTime() : 0)
      .filter(time => time > 0);
    
    if (validDates.length === 0) return 'TBD';
    
    const latestTime = Math.max(...validDates);
    return formatDate(new Date(latestTime).toISOString());
  };

  // Function to toggle pin status of a sports event
  const handlePinToggle = (eventId: string) => {
    setPinnedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
        try { posthog.capture('engagement:sports_pinned', { event_id: eventId, pinned: false }); } catch {}
      } else {
        newSet.add(eventId);
        try { posthog.capture('engagement:sports_pinned', { event_id: eventId, pinned: true }); } catch {}
      }
      return newSet;
    });
  };

  // Function to handle taking a suggested parlay
  const handleTakeParlay = (suggestion: ParlaySuggestion) => {
    // TODO: Implement the flow to add legs to parlay and proceed to quote/record
    console.log('Taking parlay:', suggestion);
    alert('Take Parlay functionality will be implemented to add these legs and place the parlay');
  };

  // Function to calculate cumulative parlay stats
  // Function to process market questions for selected event
  const processQuestionsForEvent = useCallback(async (event: EventCardType) => {
    try {
      setIsProcessingQuestions(true);
      const filteredMarkets = (event.all_markets || event.top_markets).filter(market => !isMarketResolvedOrExtreme(market));
      
      if (filteredMarkets.length > 0) {
        const questions = filteredMarkets.map(market => market.question);
        const processed = await processMarketQuestions({
          eventTitle: event.title,
          marketQuestions: questions
        });

        // Create mapping from original question to processed question
        // Skip markets that were marked as outliers (None/null values)
        const questionMap: { [key: string]: string } = {};
        questions.forEach((original, index) => {
          const processedResult = processed[index];
          if (processedResult && processedResult !== null) {
            questionMap[original] = processedResult;
          }
          // If processedResult is null/None, don't add to map (effectively filters it out)
        });
        
        setProcessedQuestions(questionMap);
      }
    } catch (error) {
      console.warn('Failed to process questions for event:', error);
      // Fallback to empty mapping - will show original questions
      setProcessedQuestions({});
    } finally {
      setIsProcessingQuestions(false);
    }
  }, []);


  const loadEventCards = useCallback(async (page: number = 1, reset: boolean = true, category?: string, subCategory?: string, filter?: string) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(1);
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      const actualCategory = category !== undefined ? category : selectedCategory;
      const actualSubCategory = subCategory !== undefined ? subCategory : selectedSubCategory;
      const actualFilter = filter !== undefined ? filter : selectedFilter;
      
      console.log('loadEventCards called with:', { actualCategory, actualSubCategory, actualFilter, page });
      
      // Handle Sports category differently
      if (actualCategory === 'Sports') {
        // Map subcategory names to API league IDs
        const leagueMapping: { [key: string]: string } = {
          'Premier League': 'EPL',
          'College Football': 'CFB',
          'NFL': 'NFL',
          'NHL': 'NHL',
          'NBA': 'NBA',
          'MLB': 'MLB',
          'CFB': 'CFB',
          'EPL': 'EPL',
          'La Liga': 'LA-LIGA',
          'LA-LIGA': 'LA-LIGA',
          'Bundesliga': 'BUNDESLIGA',
          'BUNDESLIGA': 'BUNDESLIGA',
          'Serie A': 'SERIE-A',
          'SERIE-A': 'SERIE-A',
          'Ligue 1': 'LIGUE-1',
          'LIGUE-1': 'LIGUE-1',
          'MLS': 'MLS',
          'FIFA Friendlies': 'FIFA-FRIENDLIES',
          'FIFA-FRIENDLIES': 'FIFA-FRIENDLIES',
          'Cricket': 'Cricket',
          'Tennis': 'Tennis',
          'Boxing': 'Boxing'
        };
        // For 'All' or 'Live', pass undefined to fetch all leagues; otherwise use mapped league ID
        const leagueId = (actualSubCategory && actualSubCategory !== 'Live')
          ? (leagueMapping[actualSubCategory] || actualSubCategory)
          : undefined;
        console.log('[DEBUG] Fetching sports data for league:', leagueId || 'All leagues');

        // Fetch both games and props
        // For 'All', pass undefined to fetch all props; otherwise use actualSubCategory
        const [gamesData, propsData] = await Promise.all([
          fetchSportsEvents(leagueId, 7, page, 20),
          fetchSportsProps(actualSubCategory || undefined, reset ? 1 : propsCurrentPage, 50)
        ]);

        console.log('[DEBUG] Received games:', gamesData.length, 'props:', propsData.length);

        // Filter live games if 'Live' subcategory selected
        const finalGamesData = actualSubCategory === 'Live'
          ? gamesData.filter(game => isGameLive(game))
          : gamesData;

        if (reset) {
          setSportsEvents(finalGamesData);
          setSportsProps(propsData);
        } else {
          setSportsEvents(prev => [...prev, ...finalGamesData]);
          setSportsProps(prev => [...prev, ...propsData]);
        }

        // Always check if there are any live games across all leagues to show Live category (only on first load)
        if (page === 1) {
          const allGames = await fetchSportsEvents(undefined, 7, 1, 20);
          const hasLive = allGames.some(game => isGameLive(game));
          setHasLiveGames(hasLive);
        }

        // Auto-select view: default to games, fallback to props if no games (only on first load)
        if (reset) {
          if (finalGamesData.length > 0) {
            setSportsViewMode('games');
          } else if (propsData.length > 0) {
            setSportsViewMode('props');
          }
        }

        setHasMore(gamesData.length === 20); // Enable pagination for sports games
        setPropsHasMore(propsData.length === 50); // Enable pagination for sports props
        setError(null);
      } else {
        const data = await fetchEventCards(actualCategory || undefined, actualSubCategory || undefined, page, actualFilter || undefined);
        
        if (reset) {
          setEventCards(data);
        } else {
          setEventCards(prev => [...prev, ...data]);
        }
        
        setHasMore(data.length === 100);
        console.log(`Loaded ${data.length} events, hasMore: ${data.length === 100}`);
        setError(null);
      }
    } catch (err) {
      setError(selectedCategory === 'Sports' ? 'Failed to load sports events' : 'Failed to load event cards');
      console.error('Error loading event cards:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedCategory, selectedSubCategory, selectedFilter]);

  const handleSubCategoryChange = useCallback((subCategory: string) => {
    console.log('handleSubCategoryChange called with:', subCategory);
    setSelectedSubCategory(subCategory);
    setSelectedFilter(''); // Reset filter when sub-category changes
    // Reset pagination state for both games and props
    setCurrentPage(1);
    setHasMore(true);
    setPropsCurrentPage(1);
    setPropsHasMore(true);
    // Immediately load events with the new sub-category
    loadEventCards(1, true, selectedCategory, subCategory, '');
  }, [selectedCategory, loadEventCards]);

  const handleFilterChange = useCallback((filter: string) => {
    console.log('handleFilterChange called with:', filter);
    setSelectedFilter(filter);
    // Reset pagination state for both games and props
    setCurrentPage(1);
    setHasMore(true);
    setPropsCurrentPage(1);
    setPropsHasMore(true);
    // Immediately load events with the new filter
    try {
      posthog.capture('engagement:event_filter_selected', {
        filter,
        category: selectedCategory || '',
        sub_category: selectedSubCategory || '',
      });
    } catch {}
    loadEventCards(1, true, selectedCategory, selectedSubCategory, filter);
  }, [selectedCategory, selectedSubCategory, loadEventCards]);

  const loadMoreEvents = useCallback(async () => {
    // Determine which pagination to use based on current view mode
    const isPropsView = selectedCategory === 'Sports' && sportsViewMode === 'props';
    const currentHasMore = isPropsView ? propsHasMore : hasMore;
    const currentPageNum = isPropsView ? propsCurrentPage : currentPage;

    console.log(`loadMoreEvents called - loadingMore: ${loadingMore}, hasMore: ${currentHasMore}, currentPage: ${currentPageNum}, viewMode: ${isPropsView ? 'props' : 'games'}`);

    if (!loadingMore && currentHasMore) {
      const nextPage = currentPageNum + 1;
      console.log(`Loading page ${nextPage} for ${isPropsView ? 'props' : 'games/events'}...`);

      if (isPropsView) {
        setPropsCurrentPage(nextPage);
      } else {
        setCurrentPage(nextPage);
      }
      setLoadingMore(true);

      try {
        // Wait 2 seconds before loading for better UX
        await new Promise(resolve => setTimeout(resolve, 2000));

        await loadEventCards(nextPage, false);
      } catch (error) {
        console.error('Error loading more events:', error);
        setError('Failed to load more events');
        setLoadingMore(false);
      }
    }
  }, [loadingMore, hasMore, propsHasMore, currentPage, propsCurrentPage, selectedCategory, sportsViewMode, loadEventCards]);

  useEffect(() => {
    loadEventCards(1, true);
  }, [selectedCategory, loadEventCards]);

  // Fetch parlay suggestions when on default "All" view
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (selectedCategory !== '' || !isAuthenticated) return;

      try {
        const response = await fetchParlaySuggestions();
        if (response.suggestions && response.suggestions.length > 0) {
          setParlaySuggestions(response.suggestions);
        }
      } catch (error) {
        console.error('Error fetching parlay suggestions:', error);
      }
    };

    fetchSuggestions();
  }, [selectedCategory, isAuthenticated]);

  useEffect(() => {
    // Determine which hasMore flag to use based on current view
    const isPropsView = selectedCategory === 'Sports' && sportsViewMode === 'props';
    const currentHasMore = isPropsView ? propsHasMore : hasMore;

    if (loading || !currentHasMore) return; // Don't set up observer while initial loading or when no more data

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && currentHasMore) {
          console.log('Load trigger intersecting, loading more events...');
          loadMoreEvents();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px' // Start loading when trigger is 50px away from viewport
      }
    );

    // Small delay to ensure DOM is settled
    const timeoutId = setTimeout(() => {
      const currentTrigger = loadTriggerRef.current;
      if (currentTrigger) {
        console.log('Setting up intersection observer for load trigger');
        observer.observe(currentTrigger);
      } else {
        console.warn('Load trigger ref not found');
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [loadMoreEvents, loadingMore, hasMore, propsHasMore, loading, selectedCategory, sportsViewMode]);



  // Function to open purchase modal from event card
  const openPurchaseModal = (
    eventTitle: string,
    marketQuestion: string,
    outcome: 'yes' | 'no',
    outcomePrice: number,
    marketId: string,
    endDate?: string,
    marketIcon?: string,
    eventImage?: string,
    propsLabels?: { yes: string; no: string }
  ) => {
    setPurchaseModalData({
      eventTitle,
      marketQuestion,
      outcome,
      outcomePrice,
      marketId,
      endDate,
      marketIcon,
      eventImage,
      propsLabels
    });
    setShowPurchaseModal(true);
    try {
      posthog.capture('parlay:purchase_modal_opened', {
        event_title: eventTitle,
        market_question: marketQuestion,
        outcome,
        outcome_price: outcomePrice,
        market_id: marketId,
      });
    } catch {}
  };

  // Function to handle team bet click from sports event card
  const handleSportsTeamClick = (marketId: string, teamName: string, impliedProb: number, eventTitle: string, sportsTeamData: any) => {
    setPurchaseModalData({
      eventTitle,
      marketQuestion: teamName,
      outcome: 'yes',
      outcomePrice: impliedProb,
      marketId,
      sportsTeamData,
      marketIcon: sportsTeamData?.eventImage,
    });
    setShowPurchaseModal(true);
    try {
      posthog.capture('parlay:purchase_modal_opened', {
        event_title: eventTitle,
        market_question: teamName,
        outcome: 'yes',
        outcome_price: impliedProb,
        market_id: marketId,
        is_sports: true,
      });
    } catch {}
  };

  // Function to handle market selection from MarketSelectionModal
  const handleMarketSelected = (eventTitle: string, marketQuestion: string, marketId: string, outcomePrice: number, endDate?: string, marketIcon?: string, eventImage?: string, outcome: 'yes' | 'no' = 'yes') => {
    // Close market selection modal
    setShowMarketModal(false);
    setSelectedEvent(null);
    
    // Open purchase modal with selected market data
    setPurchaseModalData({
      eventTitle,
      marketQuestion,
      outcome, // Use the actual selected outcome
      outcomePrice,
      marketId,
      endDate,
      marketIcon,
      eventImage
    });
    setShowPurchaseModal(true);
    try {
      posthog.capture('parlay:purchase_modal_opened', {
        event_title: eventTitle,
        market_question: marketQuestion,
        outcome,
        outcome_price: outcomePrice,
        market_id: marketId,
      });
    } catch {}
  };

  // Function to add a leg to the parlay (no amount until final quote)
  const addLegToParlay = async (eventTitle: string, marketQuestion: string, outcome: string, outcomePrice: number, endDate?: string, marketIcon?: string, marketId?: string, sportsTeamData?: any) => {
    try {
      if (!marketId) {
        console.error('Market ID is required');
        return false;
      }

      // Build new leg (no amount until final quote)
      const newLeg = {
        eventTitle,
        marketQuestion,
        outcome: outcome as 'yes' | 'no',
        outcomePrice,
        decimalOdds: outcomePrice > 0 ? (1 / outcomePrice) : 1.0,
        endDate,
        marketIcon,
        marketId,
        sportsTeamData
      };

      // Add leg to local state - no quote API call yet
      setParlayLegs(prev => [...prev, newLeg]);
      
      console.log('Successfully added leg to parlay:', newLeg);
      return true;
    } catch (error) {
      console.error('Error adding leg to parlay:', error);
      return false;
    }
  };

  // Function to handle "View Odds" from TotalAmountModal
  const handleViewOdds = async (totalAmount: number): Promise<boolean> => {
    try {
      setQuotingLoading(true);

      // Convert legs to quote API format
      const quoteLegs = parlayLegs.map(leg => ({
        ingest_market_id: leg.marketId,
        outcome_label: leg.outcome.toLowerCase() === 'yes' ? 'Yes' : 'No'
      }));

      // Determine wallet address for quote (use smart account if gas sponsored)
      let walletAddress = user?.walletAddress;
      if (user?.gasSponsored && user?.smartAccountAddress) {
        walletAddress = user.smartAccountAddress;
        console.log('🚀 Using smart account address for quote:', walletAddress);
      }

      // Call quote endpoint with total amount
      const response = await fetch(`${API_URL}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          issuer: user?.issuer,
          smart_contract_wallet_address: walletAddress,
          stake: totalAmount,
          legs: quoteLegs
        })
      });

      const data = await response.json();
      if (response.ok && data.quoteId) {
        // Store quote data and show confirmation
        setCurrentQuote({
          quote_id: data.quoteId,
          total_odds: data.total_odds,
          expected_payout: data.expected_payout,
          expiry: data.expires_at,
          total_amount: totalAmount
        });

        // Quote successful - TotalAmountModal will handle the rest
        
        console.log('Successfully got quote:', data);
        return true;
      } else {
        console.error('Quote API error:', data);
        return false;
      }
    } catch (error) {
      console.error('Error getting quote:', error);
      return false;
    } finally {
      setQuotingLoading(false);
    }
  };

  // Process market questions when a new event is selected
  useEffect(() => {
    if (selectedEvent) {
      // Clear previous processed questions to ensure consistency
      setProcessedQuestions({});
      processQuestionsForEvent(selectedEvent);
    }
  }, [selectedEvent, processQuestionsForEvent]);


  if (loading) return <LoadingSpinner />;
  if (error) return <div className="error">{error}</div>;

  // Helper function to detect sports game events (X vs Y format)
  const isSportsGameEvent = (event: EventCardType): boolean => {
    const title = event.title.toLowerCase();
    // Check for common sports game patterns
    return (
      /\s+vs\.?\s+/.test(event.title) || // "Team A vs Team B" or "Team A vs. Team B"
      /\s+v\s+/.test(event.title) ||     // "Team A v Team B"
      /\s+@\s+/.test(event.title) ||     // "Team A @ Team B"
      (event.category === 'Sports' && /\s+(vs|v|@)\s+/.test(event.title))
    );
  };

  const filteredEvents = eventCards.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase());
    
    // When on 'All' category, exclude sports game events (X vs Y format)
    if (selectedCategory === '' && isSportsGameEvent(event)) {
      console.log(`[All Category] Filtering out sports game event: ${event.title}`);
      return false;
    }
    
    return matchesSearch;
  });

  // Debug logging for 'All' category
  if (selectedCategory === '') {
    const sportsGames = eventCards.filter(isSportsGameEvent);
    const nonSportsGames = eventCards.filter(event => !isSportsGameEvent(event));
    console.log(`[All Category] Total events: ${eventCards.length}, Sports games: ${sportsGames.length}, Non-sports: ${nonSportsGames.length}, Filtered: ${filteredEvents.length}`);
  }

  // Show Active Parlays page if requested
  if (showActiveParlays) {
    return (
      <div className="App">
        <Parlays onBack={() => {
          setShowActiveParlays(false);
          refetchParlaysCount();
        }} />
      </div>
    );
  }

  // Show Profile page if requested
  if (showProfile) {
    return (
      <div className="App">
        <Profile onBack={() => setShowProfile(false)} />
      </div>
    );
  }

  // Show confirmation page if requested
  if (showConfirmation) {
    return (
      <div className="App">
        <Header 
          searchQuery={searchQuery} 
          onSearchChange={setSearchQuery}
          selectedCategory={selectedCategory}
          selectedSubCategory={selectedSubCategory}
          onCategoryChange={handleCategoryChange}
          onSubCategoryChange={handleSubCategoryChange}
          onShowActiveParlays={() => setShowActiveParlays(true)}
          onShowProfile={() => setShowProfile(true)}
          selectedFilter={selectedFilter}
          onFilterChange={handleFilterChange}
          referralCode={referralCode}
          hasLiveGames={hasLiveGames}
        />
        <ParlayConfirmation
          legs={parlayLegs}
          stats={parlayStats}
          onConfirm={() => {
            // Clear parlay state and return to main page
            setParlayLegs([]);
            setShowConfirmation(false);
          }}
          onAddMoreLegs={() => setShowConfirmation(false)}
          onCancelParlay={() => {
            // Clear parlay state and return to main page
            setParlayLegs([]);
            setShowConfirmation(false);
          }}
          onRefreshParlay={() => {
            // No need to recalculate - parlay data handled in confirmation
          }}
          onRemoveLegs={async (legIds: string[]) => {
            // Filter out selected legs
            const remainingLegs = parlayLegs.filter((leg, index) => {
              const legId = leg.marketId || `${leg.marketQuestion}_${index}`;
              return !legIds.includes(legId);
            });

            // Update leg state - no quote API call during building
            setParlayLegs(remainingLegs);
            
            console.log('Successfully removed legs. Remaining:', remainingLegs.length);
          }}
        />
      </div>
    );
  }

  const handleRemoveLegsFromModal = (indices: number[]) => {
    const remainingLegs = parlayLegs.filter((_, idx) => !indices.includes(idx));

    try {
      posthog.capture('parlay:legs_removed', {
        removed_count: indices.length,
        remaining_count: remainingLegs.length,
        context: 'total_amount_modal',
      });
    } catch {}

    setParlayLegs(remainingLegs);
    setCurrentQuote(null); // Clear any existing quote

    // Handle different cases:
    // - 0 legs: close modal and return to home (modal closes itself)
    // - 1 leg: keep modal open but in invalid state
    // - 2+ legs: reopen modal with valid parlay
    if (remainingLegs.length >= 2) {
      setTimeout(() => setShowTotalAmountModal(true), 100);
    } else if (remainingLegs.length === 1) {
      setTimeout(() => setShowTotalAmountModal(true), 100);
    }
    // For 0 legs, modal closes itself via handleRemoveLegs
  };

  const handleToggleBottomBannerLegSelection = (index: number) => {
    setSelectedBottomBannerLegIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleRemoveBottomBannerLegs = () => {
    if (selectedBottomBannerLegIndices.size === 0) return;

    const remainingLegs = parlayLegs.filter((_, idx) => !selectedBottomBannerLegIndices.has(idx));
    try {
      posthog.capture('parlay:legs_removed', {
        removed_count: selectedBottomBannerLegIndices.size,
        remaining_count: remainingLegs.length,
        context: 'bottom_banner',
      });
    } catch {}
    setParlayLegs(remainingLegs);

    // Reset edit state
    setIsBottomBannerEditMode(false);
    setSelectedBottomBannerLegIndices(new Set());

    // Handle banner state based on remaining legs
    if (remainingLegs.length === 0) {
      setIsBottomBannerExpanded(false);
    }
    // Keep expanded if 1 or more legs remain
  };

  const toggleBottomBannerEditMode = () => {
    setIsBottomBannerEditMode(!isBottomBannerEditMode);
    if (isBottomBannerEditMode) {
      // Exiting edit mode - clear selections
      setSelectedBottomBannerLegIndices(new Set());
    }
  };

  return (
    <div className="App">
      {/* Top Banner for Active Parlays */}
      {activeParlaysCount > 0 && (
        <div className="top-banner">
          <span>You have {activeParlaysCount} active parlay{activeParlaysCount !== 1 ? 's' : ''}. </span>
          <button 
            className="view-now-btn"
            onClick={() => { setShowActiveParlays(true); try { posthog.capture('parlay:view'); } catch {} }}
          >
          <strong>View now</strong>
          </button>
        </div>
      )}
      
      <Header 
        searchQuery={searchQuery} 
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        selectedSubCategory={selectedSubCategory}
        onCategoryChange={handleCategoryChange}
        onSubCategoryChange={handleSubCategoryChange}
        onShowActiveParlays={() => setShowActiveParlays(true)}
        onShowProfile={() => setShowProfile(true)}
        selectedFilter={selectedFilter}
        onFilterChange={handleFilterChange}
        referralCode={referralCode}
        hasLiveGames={hasLiveGames}
        sportsEvents={sportsEvents}
        sportsProps={sportsProps}
      />
      
      <main className="App-main">
        {selectedCategory === 'Sports' ? (
          <div className="sports-layout-container">
            <SportsNavSidebar
              selectedSubCategory={selectedSubCategory}
              onSubCategoryChange={handleSubCategoryChange}
              hasLiveGames={hasLiveGames}
            />
            <div className="sports-main-content">
              <div className="sports-events-container">
                {/* Sport/League Heading */}
                <div className="sports-heading">
                  <h1 className="sports-title">
                    {selectedSubCategory === 'Live' 
                      ? 'Live'
                      : (selectedSubCategory || 'All Sports')
                    }
                  </h1>
                </div>
                
                {/* Games/Props Toggle - only show if both are available */}
                {sportsEvents.length > 0 && sportsProps.length > 0 && (
                  <div className="sports-view-toggle">
                    <button
                      className={`toggle-button ${sportsViewMode === 'games' ? 'active' : ''}`}
                      onClick={() => { setSportsViewMode('games'); try { posthog.capture('engagement:sports_tab', { tab: 'games' }); } catch {} }}
                    >
                      Games ({sportsEvents.length})
                    </button>
                    <button
                      className={`toggle-button ${sportsViewMode === 'props' ? 'active' : ''}`}
                      onClick={() => { setSportsViewMode('props'); try { posthog.capture('engagement:sports_tab', { tab: 'props' }); } catch {} }}
                    >
                      Props ({sportsProps.length})
                    </button>
                  </div>
                )}

            {/* Render based on view mode */}
            {sportsViewMode === 'games' ? (
              <>
                {Object.entries(groupEventsWithPinned(sportsEvents, pinnedEvents, selectedSubCategory)).map(([groupName, events]) => (
                  <div key={groupName}>
                    <h2 className="sports-date-header">{groupName}</h2>
                    <div className="sports-cards-grid">
                      {events
                        .filter(event => {
                          // Filter out games where ALL teams have extreme probabilities (≤0.5% or ≥99.5%)
                          const hasValidTeam = event.teams.some(team => {
                            const prob = team.implied_prob;
                            // Team is valid if probability is between 0.5% and 99.5%
                            return prob > 0.005 && prob < 0.995;
                          });
                          return hasValidTeam; // Only show games with at least one valid team
                        })
                        .map((event) => (
                          <SportsEventCard
                            key={event.id}
                            event={event}
                            onTeamClick={handleSportsTeamClick}
                            isPinned={pinnedEvents.has(event.id)}
                            onPinToggle={handlePinToggle}
                            parlayLegs={parlayLegs}
                          />
                        ))}
                    </div>
                  </div>
                ))}
                {sportsEvents.length === 0 && !loading && (
                  <div className="empty-state">
                    <p>No {selectedSubCategory || 'NFL'} games available</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Props view - use EventCard grid (3 columns) */}
                <div className="sports-props-grid" style={{ marginTop: '20px' }}>
                  {sportsProps.map((event, index) => (
                    <EventCard
                      key={index}
                      event={event}
                      onOpenPurchaseModal={openPurchaseModal}
                      parlayLegs={parlayLegs}
                      onClick={(event) => {
                        if (isSingleMarketEvent(event)) {
                          const market = event.top_markets[0] as any;
                          const yesPrice = market.outcome_prices[0] || 0;
                          const noPrice = market.outcome_prices[1] || 0;
                          // Tennis labels for modal
                          const tennisLabels = (selectedSubCategory === 'Tennis' && /\svs\.?\s/i.test(event.title)) ? (() => {
                            const parts = event.title.split(/\svs\.?\s/i);
                            const getSurname = (s: string) => {
                              const tokens = s.trim().split(/\s+/); return tokens[tokens.length - 1];
                            };
                            return { yes: getSurname(parts[0]), no: getSurname(parts[1]) };
                          })() : undefined;
                          const cricketLabels = (!tennisLabels && selectedSubCategory === 'Cricket' && /\svs\.?\s/i.test(event.title)) ? (() => {
                            const parts = event.title.split(/\svs\.?\s/i);
                            const stripPrefix = (s: string) => s.replace(/^.*?:\s*/, '').trim();
                            return { yes: stripPrefix(parts[0]), no: stripPrefix(parts[1]) };
                          })() : undefined;

                          setPurchaseModalData({
                            eventTitle: event.title,
                            marketQuestion: market.processed_question || market.question,
                            outcome: 'yes',
                            outcomePrice: yesPrice,
                            marketId: (market.market_id || market.id),
                            endDate: (market.ends_at || market.end_date),
                            marketIcon: market.icon,
                            propsLabels: tennisLabels || cricketLabels
                          });
                          setShowPurchaseModal(true);
                        } else {
                          // For multi-market events, open MarketSelectionModal
                          setSelectedEvent(event);
                          setShowMarketModal(true);
                          setSelectedMarket(null);
                          setSelectedOutcome('yes');
                          processQuestionsForEvent(event);
                        }
                      }}
                    />
                  ))}
                </div>
                {sportsProps.length === 0 && !loading && (
                  <div className="empty-state">
                    <p>No {selectedSubCategory || 'NFL'} props available</p>
                  </div>
                )}
              </>
            )}
              </div>
            </div>
          </div>
        ) : selectedCategory === 'Mentions' ? (
          <>
            {parlaySuggestions.length > 0 && selectedFilter === 'Trending' && (
              <ParlaySuggestionsCarousel
                suggestions={parlaySuggestions}
                onTakeParlay={handleTakeParlay}
              />
            )}
            <div className="mentions-events-container">
              <div className="mentions-header">
                <h1>Mention Markets</h1>
                <p>Earn based on what others say</p>
              </div>
              {filteredEvents
                .sort((a, b) => {
                  // Sort by ends_at date (earliest first), but put events without ends_at at the bottom
                  const hasDateA = !!a.ends_at;
                  const hasDateB = !!b.ends_at;
                  
                  // If one has a date and the other doesn't, prioritize the one with a date
                  if (hasDateA && !hasDateB) return -1;
                  if (!hasDateA && hasDateB) return 1;
                  
                  // If both have dates, sort by date (earliest first)
                  if (hasDateA && hasDateB) {
                    const dateA = new Date(a.ends_at!).getTime();
                    const dateB = new Date(b.ends_at!).getTime();
                    return dateA - dateB;
                  }
                  
                  // If neither has a date, maintain original order
                  return 0;
                })
                .map((event, index) => (
                <MentionsEventCard
                  key={index}
                  event={event}
                  parlayLegs={parlayLegs}
                  onOpenMarketModal={(event) => {
                    setSelectedEvent(event);
                    setShowMarketModal(true);
                    setSelectedMarket(null);
                    setSelectedOutcome('yes');
                    processQuestionsForEvent(event);
                  }}
                  onMarketClick={(event, marketIndex) => {
                    // Get active markets (filter out resolved/extreme ones) - use all_markets if available
                    const markets = event.all_markets || event.top_markets;
                    const activeMarkets = markets.filter(market => {
                      const yesPrice = market.outcome_prices[0] || 0;
                      const noPrice = market.outcome_prices[1] || 0;
                      // Filter out resolved/extreme markets (≤0.1% or ≥99.9%)
                      return !(yesPrice <= 0.001 || yesPrice >= 0.999 ||
                               noPrice <= 0.001 || noPrice >= 0.999);
                    });

                    const market = activeMarkets[marketIndex];
                    if (market) {
                      openPurchaseModal(
                        event.title,
                        market.question,
                        'yes',
                        market.outcome_prices[0] || 0,
                        market.market_id || '',
                        market.end_date,
                        market.icon,
                        event.image
                      );
                    }
                  }}
                />
              ))}
            </div>
          </>
        ) : selectedCategory === 'Earnings' ? (
          <>
            <EarningsCalendar
              events={filteredEvents}
              onEventClick={(event) => {
                // Open market selection modal for earnings events
                setSelectedEvent(event);
                setShowMarketModal(true);
                setSelectedMarket(null);
                setSelectedOutcome('yes');
                processQuestionsForEvent(event);
              }}
            />
          </>
        ) : (
          <>
            {parlaySuggestions.length > 0 && selectedFilter === 'Trending' && (
              <ParlaySuggestionsCarousel
                suggestions={parlaySuggestions}
                onTakeParlay={handleTakeParlay}
              />
            )}
            {selectedCategory === 'Finance' ? (
              <div className="sports-layout-container">
                <FinanceNavSidebar
                  selectedSubCategory={selectedSubCategory}
                  onSubCategoryChange={handleSubCategoryChange}
                  counts={financeCounts}
                  totalCount={financeTotal}
                />
                <div className="sports-main-content">
                  <div className="sports-events-container">
                    <div className="event-cards-container finance-grid">
                      {filteredEvents.map((event, index) => (
                        <EventCard
                          key={index}
                          event={event}
                          parlayLegs={parlayLegs}
                          onClick={(event) => {
                            if (isSingleMarketEvent(event)) {
                              const activeMarket = event.top_markets.filter(market => {
                                const yesPrice = market.outcome_prices[0] || 0;
                                const noPrice = market.outcome_prices[1] || 0;
                                return !(yesPrice <= 0.001 || yesPrice >= 0.999 ||
                                         noPrice <= 0.001 || noPrice >= 0.999);
                              })[0];

                              if (activeMarket && activeMarket.market_id) {
                                openPurchaseModal(
                                  event.title,
                                  activeMarket.question,
                                  'yes',
                                  activeMarket.outcome_prices[0] || 0,
                                  activeMarket.market_id,
                                  activeMarket.end_date,
                                  activeMarket.icon || event.image
                                );
                              }
                            } else {
                              setSelectedEvent(event);
                              setShowMarketModal(true);
                              setSelectedMarket(null);
                              setSelectedOutcome('yes');
                            }
                          }}
                          isSelected={false}
                          onOpenPurchaseModal={openPurchaseModal}
                          isNew={selectedFilter === 'Newest'}
                          isTrending={selectedFilter === 'Trending'}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : selectedCategory === 'Crypto' ? (
              <div className="sports-layout-container">
                <CryptoNavSidebar
                  selectedSubCategory={selectedSubCategory}
                  onSubCategoryChange={handleSubCategoryChange}
                />
                <div className="sports-main-content">
                  <div className="sports-events-container">
                    <div className="event-cards-container crypto-grid">
                      {filteredEvents.map((event, index) => (
                        <EventCard
                          key={index}
                          event={event}
                          parlayLegs={parlayLegs}
                          onClick={(event) => {
                            if (isSingleMarketEvent(event)) {
                              const activeMarket = event.top_markets.filter(market => {
                                const yesPrice = market.outcome_prices[0] || 0;
                                const noPrice = market.outcome_prices[1] || 0;
                                return !(yesPrice <= 0.001 || yesPrice >= 0.999 ||
                                         noPrice <= 0.001 || noPrice >= 0.999);
                              })[0];

                              if (activeMarket && activeMarket.market_id) {
                                openPurchaseModal(
                                  event.title,
                                  activeMarket.question,
                                  'yes',
                                  activeMarket.outcome_prices[0] || 0,
                                  activeMarket.market_id,
                                  activeMarket.end_date,
                                  activeMarket.icon || event.image
                                );
                              }
                            } else {
                              setSelectedEvent(event);
                              setShowMarketModal(true);
                              setSelectedMarket(null);
                              setSelectedOutcome('yes');
                            }
                          }}
                          isSelected={false}
                          onOpenPurchaseModal={openPurchaseModal}
                          isNew={selectedFilter === 'Newest'}
                          isTrending={selectedFilter === 'Trending'}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="event-cards-container">
            {filteredEvents.map((event, index) => (
              <EventCard
                key={index}
                event={event}
                parlayLegs={parlayLegs}
                onClick={(event) => {
                  if (isSingleMarketEvent(event)) {
                    // For single-market events, open PurchaseModal directly
                    const activeMarket = event.top_markets.filter(market => {
                      const yesPrice = market.outcome_prices[0] || 0;
                      const noPrice = market.outcome_prices[1] || 0;
                      // Filter out resolved/extreme markets (≤0.1% or ≥99.9%)
                      return !(yesPrice <= 0.001 || yesPrice >= 0.999 ||
                               noPrice <= 0.001 || noPrice >= 0.999);
                    })[0];

                    if (activeMarket && activeMarket.market_id) {
                      openPurchaseModal(
                        event.title,
                        activeMarket.question,
                        'yes', // Default to Yes
                        activeMarket.outcome_prices[0] || 0,
                        activeMarket.market_id,
                        activeMarket.end_date,
                        activeMarket.icon || event.image
                      );
                    }
                  } else {
                    // For multi-market events, open MarketSelectionModal
                    setSelectedEvent(event);
                    setShowMarketModal(true);
                    setSelectedMarket(null);
                    setSelectedOutcome('yes');
                  }
                }}
                isSelected={false}
                onOpenPurchaseModal={openPurchaseModal}
                isNew={selectedFilter === 'Newest'}
                isTrending={selectedFilter === 'Trending'}
              />
            ))}
          </div>
            )}
          </>
        )}
        {loadingMore && <LoadingDots />}
        {hasMore && (
          <div 
            ref={loadTriggerRef} 
            style={{ 
              height: '20px', 
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        )}
        
        {/* Market Selection Modal */}
        <MarketSelectionModal
          isOpen={showMarketModal}
          onClose={() => {
            setShowMarketModal(false);
            setSelectedEvent(null);
            setSelectedMarket(null);
            setSelectedOutcome('yes');
          }}
          selectedEvent={selectedEvent}
          processedQuestions={processedQuestions}
          isProcessingQuestions={isProcessingQuestions}
          isMarketResolved={isMarketResolvedOrExtreme}
          onMarketSelected={handleMarketSelected}
        />

        {/* Purchase Modal */}
        {purchaseModalData && (
          <PurchaseModal
            isOpen={showPurchaseModal}
            onClose={() => {
              setShowPurchaseModal(false);
              setPurchaseModalData(null);
            }}
            eventTitle={purchaseModalData.eventTitle}
            marketQuestion={purchaseModalData.marketQuestion}
            selectedOutcome={purchaseModalData.outcome}
            outcomePrice={purchaseModalData.outcomePrice}
            marketIcon={purchaseModalData.marketIcon}
            eventImage={purchaseModalData.eventImage}
            addingLegLoading={addingLegLoading}
            parlayLegs={parlayLegs}
            onAddLegToParlay={addLegToParlay}
            formatNumberWithCommas={formatNumberWithCommas}
            calculateSettlementDate={calculateSettlementDate}
            marketId={purchaseModalData.marketId}
            endDate={purchaseModalData.endDate}
            sportsTeamData={purchaseModalData.sportsTeamData}
            propsLabels={purchaseModalData.propsLabels}
          />
        )}

        {/* Total Amount Modal */}
        <TotalAmountModal
          isOpen={showTotalAmountModal}
          onClose={() => setShowTotalAmountModal(false)}
          parlayLegs={parlayLegs}
          onViewOdds={handleViewOdds}
          onConfirm={() => {
            setShowTotalAmountModal(false);
            setParlayLegs([]);
            setCurrentQuote(null);
            setShowNotificationBanner(true);
            refetchParlaysCount();
          }}
          isLoading={quotingLoading}
          formatNumberWithCommas={formatNumberWithCommas}
          calculateSettlementDate={calculateSettlementDate}
          currentQuote={currentQuote}
          onRemoveLegs={handleRemoveLegsFromModal}
        />

        {/* Parlay Confirmation Modal */}
        <ParlayConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          currentQuote={currentQuote}
          parlayLegs={parlayLegs}
          onConfirm={() => {
            setShowConfirmationModal(false);
            setParlayLegs([]);
            setCurrentQuote(null);
            setShowNotificationBanner(true);
            refetchParlaysCount();
          }}
          formatNumberWithCommas={formatNumberWithCommas}
        />

        {/* Notification Banner */}
        {showNotificationBanner && (
          <NotificationBanner
            message="Your parlay has been placed! View it"
            onLinkClick={() => {
              setShowNotificationBanner(false);
              try { posthog.capture('engagement:notification_clicked'); } catch {}
              setShowActiveParlays(true);
            }}
            onClose={() => setShowNotificationBanner(false)}
          />
        )}

        {/* How It Works Modal */}
        <HowItWorksModal
          isOpen={showHowItWorksModal}
          onClose={() => setShowHowItWorksModal(false)}
          onOpenLogin={() => {}}
        />

        {/* Get 5 TPC Modal */}
        <Get5TPCModal
          isOpen={showGet5TPCModal}
          onClose={() => setShowGet5TPCModal(false)}
        />

        {/* Get 15 TPC Modal */}
        <Get15TPCModal
          isOpen={showGet15TPCModal}
          onClose={() => setShowGet15TPCModal(false)}
          onOpenLogin={() => {
            // Set context for signup from Get15TPC modal
            sessionStorage.setItem('authContext', 'home-page');
            sessionStorage.setItem('postAuthRedirectUrl', '/');
            sessionStorage.setItem('isSignup', 'true');
            setShowLoginModal(true);
          }}
        />

        {/* Login Modal */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          referralCode={referralCode}
          isSignup={sessionStorage.getItem('isSignup') === 'true'}
          onRequireSignup={() => {
            sessionStorage.setItem('isSignup', 'true');
            // Modal will re-render with isSignup=true
          }}
        />
      </main>

      {/* Bottom Banner - Simplified */}
      <div className={`bottom-banner ${isBottomBannerExpanded ? 'expanded' : ''} ${parlayLegs.length > 1 && !isBottomBannerExpanded ? 'has-place-parlay-btn' : ''}`}>
        {/* Dropdown legs display - shown when expanded */}
        {isBottomBannerExpanded && parlayLegs.length > 0 && (
          <div className="bottom-banner-dropdown">
            {parlayLegs.map((leg, index) => {
              // Determine icon source: sports team logo or market icon
              const isSportsLeg = leg.sportsTeamData;
              let displayIcon = leg.marketIcon;
              let isDrawBet = false;

              if (isSportsLeg) {
                isDrawBet = leg.sportsTeamData.selectedTeam === 0;
                if (!isDrawBet) {
                  const selectedTeam = leg.sportsTeamData.selectedTeam === 1
                    ? leg.sportsTeamData.team1
                    : leg.sportsTeamData.team2;
                  displayIcon = selectedTeam.logo || leg.marketIcon;
                }
              }

              return (
                <div key={`${leg.marketId}-${index}`} className="bottom-banner-leg">
                  <div className="bottom-banner-leg-content">
                    {isBottomBannerEditMode && (
                      <input
                        type="checkbox"
                        checked={selectedBottomBannerLegIndices.has(index)}
                        onChange={() => handleToggleBottomBannerLegSelection(index)}
                        className="bottom-banner-leg-checkbox"
                      />
                    )}
                    {isDrawBet ? (
                      <div className="bottom-banner-leg-icon-draw">
                        {leg.sportsTeamData.team1?.logo && (
                          <img src={leg.sportsTeamData.team1.logo} alt={leg.sportsTeamData.team1.name} className="bottom-banner-leg-icon-small" />
                        )}
                        {leg.sportsTeamData.team2?.logo && (
                          <img src={leg.sportsTeamData.team2.logo} alt={leg.sportsTeamData.team2.name} className="bottom-banner-leg-icon-small" />
                        )}
                      </div>
                    ) : displayIcon ? (
                      <img src={displayIcon} alt={leg.eventTitle} className="bottom-banner-leg-icon" />
                    ) : null}
                    <div className="bottom-banner-leg-info">
                      <div className="bottom-banner-leg-title">{leg.eventTitle.replace(/ICC\s+\w+:\s*/i, '')}</div>
                      <div className="bottom-banner-leg-question">{leg.marketQuestion}</div>
                    </div>
                    <div className="bottom-banner-leg-price">
                      {(() => {
                        const p = leg.outcomePrice || 0;
                        if (p > 0 && p < 0.01) {
                          const cents = Math.floor(p * 10000) / 100;
                          const val = cents <= 0 ? 0.01 : cents;
                          return val.toFixed(2);
                        }
                        if (p >= 0.99 && p < 1) {
                          const truncatedTwoDecimals = Math.floor(p * 10000) / 100;
                          return truncatedTwoDecimals.toFixed(2);
                        }
                        return Math.round(p * 100).toString();
                      })()}¢
                    </div>
                    <div className="bottom-banner-leg-outcome">
                      {(() => {
                        const isSportsLeg = !!leg.sportsTeamData;
                        const isHeadToHeadProp = !isSportsLeg && /\svs\.?\s/i.test(leg.eventTitle) && /Tennis|Cricket/i.test(leg.eventTitle);
                        const badgeOutcome = (isSportsLeg || isHeadToHeadProp) ? 'yes' : leg.outcome;
                        return (
                          <span className={`bottom-banner-outcome-badge ${badgeOutcome}`}>
                            {badgeOutcome === 'yes' ? 'Yes' : 'No'}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  {index < parlayLegs.length - 1 && <div className="bottom-banner-leg-divider" />}
                </div>
              );
            })}
          </div>
        )}

        <div className="bottom-banner-track">
          {/* Action buttons - only visible on mobile via CSS */}
          {parlayLegs.length === 0 && (
            <>
              <button className="bottom-banner-action-btn" onClick={() => { setShowHowItWorksModal(true); try { posthog.capture('engagement:howtpcworks'); } catch {} }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                How TPC works
              </button>
              <button className="bottom-banner-action-btn" onClick={() => { setShowGet15TPCModal(true); try { posthog.capture('engagement:15tpc'); } catch {} }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                Get 15 $TPC
              </button>
            </>
          )}

          {/* Parlay legs info - always visible on desktop, hidden on mobile when no legs */}
          <span className={`bottom-banner-item ${parlayLegs.length === 0 ? 'hide-on-mobile-no-legs' : ''}`}>
            <span className="bottom-banner-label">Parlay Legs:</span>
            <span className="bottom-banner-value">
              {parlayLegs.length} {parlayLegs.length === 1 ? 'leg' : 'legs'} selected
            </span>
          </span>

          {/* Toggle button - shown when 1+ legs */}
          {parlayLegs.length >= 1 && (
            <button
              className="bottom-banner-toggle-btn"
              onClick={() => { const next = !isBottomBannerExpanded; try { posthog.capture('engagement:bottom_banner_toggle', { expanded: next }); } catch {} setIsBottomBannerExpanded(next); }}
            >
              <svg
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                className={`bottom-banner-chevron ${isBottomBannerExpanded ? 'expanded' : ''}`}
              >
                <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"/>
              </svg>
            </button>
          )}

          {/* Edit/Restore button - shown when expanded */}
          {isBottomBannerExpanded && parlayLegs.length >= 1 && (
            <button
              className="bottom-banner-edit-btn"
              onClick={toggleBottomBannerEditMode}
            >
              {isBottomBannerEditMode ? 'Restore' : 'Edit'}
            </button>
          )}

          {/* Place parlay / Remove legs button */}
          {parlayLegs.length > 1 && !isBottomBannerEditMode && (
            <button
              className="place-parlay-btn"
              onClick={() => { setShowTotalAmountModal(true); try { posthog.capture('parlay:place_parlay_clicked', { legs_count: parlayLegs.length }); } catch {} }}
            >
              Place Parlay
            </button>
          )}
          {isBottomBannerEditMode && selectedBottomBannerLegIndices.size > 0 && (
            <button
              className={`place-parlay-btn remove-legs-btn ${selectedBottomBannerLegIndices.size > 1 ? 'plural' : ''}`}
              onClick={handleRemoveBottomBannerLegs}
            >
              Remove {selectedBottomBannerLegIndices.size === 1 ? 'Leg' : 'Legs'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  // Handle OAuth callback route for redirect flow
  if (window.location.pathname === '/oauth/callback') {
    return (
      <AuthProvider>
        <OAuthCallback />
      </AuthProvider>
    );
  }

  // Main app
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
